import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { ToggleButtonChangeEvent } from 'primereact/togglebutton';
import { DropdownProps } from 'primereact/dropdown';
import { PRODUCT_LIST } from '../constants/queryKeys';
import useAxiosPrivate from './useAxiosPrivate';
import { Page } from '../api/_generated_';
import useDebounce from './useDebounce';

const STORAGE_KEY = 'products_filters';

type Filters = {
  page: number;
  rows: number;
  keyword: string;
  productNumber: string;
  hasPendingChanges: boolean;
  isDolly: boolean;
  onlyDollyPossibilities: boolean;
  status: string | null;
  lifecycle: string | null;
};

export default function useProducts() {
  const axios = useAxiosPrivate();

  const storedFilters = useMemo<Filters>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw
        ? JSON.parse(raw)
        : {
          page: 0,
          rows: 20,
          keyword: '',
          productNumber: '',
          hasPendingChanges: false,
          isDolly: false,
          onlyDollyPossibilities: false,
          status: null,
          lifecycle: null,
        };
    } catch {
      return {
        page: 0,
        rows: 20,
        keyword: '',
        productNumber: '',
        hasPendingChanges: false,
        isDolly: false,
        onlyDollyPossibilities: false,
        status: null,
        lifecycle: null,
      };
    }
  }, []);

  const [filters, setFilters] = useState<Filters>(storedFilters);

  const debouncedKeyword = useDebounce(filters.keyword, 500);
  const debouncedProductNumber = useDebounce(filters.productNumber, 500);

  async function getProducts({ queryKey }: any): Promise<Page> {
    const {
      page,
      rows,
      keyword,
      hasPendingChanges,
      isDolly,
      productNumber,
      productStatus,
      productLifecycle,
      onlyDollyPossibilities,
    } = queryKey[1];

    const urlParams = new URLSearchParams();
    urlParams.append('page', page.toString());
    urlParams.append('size', rows.toString());
    urlParams.append('hasPendingChanges', String(hasPendingChanges));
    urlParams.append('isDolly', String(isDolly));
    urlParams.append('onlyDollyPossibilities', String(onlyDollyPossibilities));

    if (productNumber) urlParams.append('productNumber', productNumber);
    if (keyword) urlParams.append('search', keyword);
    if (productStatus) urlParams.append('status', productStatus);
    if (productLifecycle) urlParams.append('lifecycle', productLifecycle);

    const res = await axios.get<Page>(`/products?${urlParams.toString()}`);
    return res.data;
  }

  const { data: result, status, isFetching } = useQuery({
    queryKey: [
      PRODUCT_LIST,
      {
        page: filters.page,
        rows: filters.rows,
        keyword: debouncedKeyword,
        hasPendingChanges: filters.hasPendingChanges,
        isDolly: filters.isDolly,
        productNumber: debouncedProductNumber,
        productStatus: filters.status,
        productLifecycle: filters.lifecycle,
        onlyDollyPossibilities: filters.onlyDollyPossibilities,
      },
    ],
    queryFn: getProducts,
    keepPreviousData: true,
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilters = (updates: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const onChangePage = (e: PaginatorPageChangeEvent) => {
    updateFilters({ page: e.page, rows: e.rows });
  };

  const onChangeStatus = ({ value }: DropdownProps) => {
    updateFilters({ status: value ?? null, page: 0 });
  };

  const onChangeLifecycle = ({ value }: DropdownProps) => {
    updateFilters({ lifecycle: value ?? null, page: 0 });
  };

  const onChangeKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilters({ keyword: e.target.value, page: 0 });
  };

  const onChangeProductNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilters({ productNumber: e.target.value, page: 0 });
  };

  const onChangePendingChangeFilter = (e: ToggleButtonChangeEvent) => {
    updateFilters({ hasPendingChanges: e.value, page: 0 });
  };

  const onChangeDollyFilter = (e: ToggleButtonChangeEvent) => {
    updateFilters({ isDolly: e.value, page: 0 });
  };

  const onChangeOnlyDollyPossibilities = (e: ToggleButtonChangeEvent) => {
    updateFilters({ onlyDollyPossibilities: e.value, page: 0 });
  };

  const handleClearFilter = () => {
    const cleared: Filters = {
      page: 0,
      rows: filters.rows,
      keyword: '',
      productNumber: '',
      hasPendingChanges: false,
      isDolly: false,
      onlyDollyPossibilities: false,
      status: null,
      lifecycle: null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleared));
    setFilters(cleared);
  };

  return {
    isFetching,
    status,
    result,
    keyword: filters.keyword,
    hasPendingChanges: filters.hasPendingChanges,
    isDolly: filters.isDolly,
    onlyDollyPossibilities: filters.onlyDollyPossibilities,
    pageState: {
      first: filters.page * filters.rows,
      rows: filters.rows,
      page: filters.page,
      pageCount: 0,
    },
    productNumber: filters.productNumber,
    productStatus: filters.status,
    productLifecycle: filters.lifecycle,
    onChangeStatus,
    onChangePage,
    onChangeKeyword,
    onChangePendingChangeFilter,
    onChangeDollyFilter,
    onChangeOnlyDollyPossibilities,
    onChangeProductNumber,
    onChangeLifecycle,
    handleClearFilter,
  };
}
